import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDebounce } from '@/hooks/useDebounce';
import { 
  getSearchResults,
  getPopularSearchTerms, 
  recordSuggestionClick 
} from '@/lib/services/search-service';
import { Trek } from '@/lib/types/trek-types';
import SearchInput from './SearchInput';
import PopularSearches from './PopularSearches';
import ExpandedSearchOverlay from './ExpandedSearchOverlay';
import { SeoDataService } from '@/lib/services/seo-data-service';

const HeroSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [trekResults, setTrekResults] = useState<Trek[]>([]);
  const [popularSearches, setPopularSearches] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPopular, setIsLoadingPopular] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  const isMobile = useIsMobile();
  const analytics = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopularSearches = async () => {
      setIsLoadingPopular(true);
      try {
        const terms = await getPopularSearchTerms();
        setPopularSearches(terms);
      } catch (error) {
        console.error('Error fetching popular search terms:', error);
        setPopularSearches(['Kedarkantha', 'Valley of Flowers', 'Hampta Pass']);
      } finally {
        setIsLoadingPopular(false);
      }
    };
    
    fetchPopularSearches();
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (debouncedSearchQuery.trim().length > 1) {
        setIsLoading(true);
        try {
          const results = await getSearchResults(debouncedSearchQuery);
          setSuggestions(results.suggestions);
          setTrekResults(results.treks);
          setHighlightedIndex(-1);
        } catch (error) {
          console.error('Error fetching search results:', error);
          setSuggestions([]);
          setTrekResults([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setTrekResults([]);
      }
    };

    fetchSearchResults();
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  const getTotalResults = () => suggestions.length + trekResults.length;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isExpanded || getTotalResults() === 0) return;

    const totalItems = getTotalResults();
    const suggestionCount = suggestions.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0) {
        e.preventDefault();
        if (highlightedIndex < suggestionCount) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          const trekIndex = highlightedIndex - suggestionCount;
          handleTrekResultClick(trekResults[trekIndex].id);
        }
      } else if (searchQuery.trim()) {
        handleSearchSubmit();
      }
    }
  };

  const trackSearch = (query: string) => {
    setSearchQuery(query);
    analytics.track('Homepage Search', { query });
  };

  const handleSearchSubmit = () => {
    if (!searchQuery.trim()) return;
    
    analytics.track('Hero Search Button Clicked', {
      query: searchQuery
    });
    
    SeoDataService.recordSearchQuery(searchQuery, -1, 'hero_search');
    
    recordSuggestionClick(searchQuery);
    
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    setIsExpanded(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    
    recordSuggestionClick(suggestion);
    
    analytics.track('Search Suggestion Clicked', { suggestion });
    
    SeoDataService.recordSearchQuery(suggestion, -1, 'suggestion_click');
    
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    setIsExpanded(false);
  };

  const handleTrekResultClick = (trekId: string) => {
    analytics.track('Trek Search Result Clicked', { 
      trek_id: trekId,
      query: searchQuery
    });
    
    navigate(`/trek/${trekId}`);
    setIsExpanded(false);
  };

  const trackPopularSearch = (term: string) => {
    recordSuggestionClick(term);
    
    analytics.track('Popular Search Clicked', { term });
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setIsExpanded(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setTrekResults([]);
    searchInputRef.current?.focus();
  };

  const openExpandedSearch = () => {
    setIsExpanded(true);
    analytics.track('Expanded Search Opened');
  };

  const closeExpandedSearch = () => {
    setIsExpanded(false);
    analytics.track('Expanded Search Closed', { method: 'backdrop_click' });
  };

  return (
    <div className="w-full mt-8 animate-fade-up" ref={searchContainerRef}>
      <div className="mx-auto max-w-xl">
        <div className="relative">
          <div onClick={openExpandedSearch}>
            <SearchInput 
              searchQuery={searchQuery}
              onSearchChange={trackSearch}
              onSearchSubmit={handleSearchSubmit}
              onClearSearch={clearSearch}
              onFocus={openExpandedSearch}
              onKeyDown={handleKeyDown}
              inputRef={searchInputRef}
              isMobile={isMobile}
            />
          </div>

          <PopularSearches 
            isLoading={isLoadingPopular}
            popularSearches={popularSearches}
            onSearchClick={trackPopularSearch}
          />
        </div>
      </div>

      {isExpanded && (
        <ExpandedSearchOverlay
          searchQuery={searchQuery}
          onSearchChange={trackSearch}
          onSearchSubmit={handleSearchSubmit}
          onClearSearch={clearSearch}
          onClose={closeExpandedSearch}
          onKeyDown={handleKeyDown}
          inputRef={searchInputRef}
          isLoading={isLoading}
          isLoadingPopular={isLoadingPopular}
          suggestions={suggestions}
          trekResults={trekResults}
          popularSearches={popularSearches}
          highlightedIndex={highlightedIndex}
          onSuggestionClick={handleSuggestionClick}
          onTrekResultClick={handleTrekResultClick}
          onPopularSearchClick={trackPopularSearch}
          onMouseEnterSuggestion={setHighlightedIndex}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

export default HeroSearch;
